import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUpRight, ArrowDownRight, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Layers } from "lucide-react";
import { formatPrice, formatQty, formatKstTime } from "@/lib/utils";

interface TradeHistory {
  id: number;
  bot_id: number;
  bot_name: string;
  symbol: string;
  side: string;
  strategy_type: string;
  bot_source?: string;
  pnl: number;
  fee: number;
  net_pnl: number;
  price: number;
  qty: number;
  leverage: number;
  reason: string;
  created_at: string;
  is_live: boolean;
  use_testnet: boolean;
}

interface SignalHistoryProps {
  onSelectBot?: (id: string) => void;
}

export const SignalHistory: React.FC<SignalHistoryProps> = ({ onSelectBot }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const MODE_KEY = 'signal_history_mode';
  const BOT_KEY = 'signal_history_bot';
  const STRATEGY_KEY = 'signal_history_strategy';
  const STRATEGY_TYPE_KEY = 'signal_history_strategy_type';

  const [histories, setHistories] = useState<TradeHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'all' | 'mainnet' | 'testnet' | 'paper'>(() => {
    return (localStorage.getItem(MODE_KEY) as any) || 'all';
  });
  const [selectedBot, setSelectedBot] = useState<string>(() => localStorage.getItem(BOT_KEY) || 'all');
  const [strategyFilter, setStrategyFilter] = useState<'all' | 'manual' | 'auto'>(() => {
    return (localStorage.getItem(STRATEGY_KEY) as any) || 'all';
  });
  const [strategyType, setStrategyType] = useState<string>(() => localStorage.getItem(STRATEGY_TYPE_KEY) || 'all');
  const [availableStrategies, setAvailableStrategies] = useState<string[]>([]);
  
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(30);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(BOT_KEY, selectedBot);
    localStorage.setItem(STRATEGY_KEY, strategyFilter);
    localStorage.setItem(STRATEGY_TYPE_KEY, strategyType);
  }, [mode, selectedBot, strategyFilter, strategyType]);

  const fetchData = async (p = page, isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const res = await fetch(`/signal/trades_history?mode=${mode}&bot_id=${selectedBot}&page=${p}&per=${per}&strategy_filter=${strategyFilter}&strategy_type=${strategyType}`);
      const data = await res.json();
      setHistories(data.histories || []);
      setTotal(data.total || 0);
      setPage(data.page || p);
      setPer(data.per || 30);
      if (data.available_strategies) setAvailableStrategies(data.available_strategies);
    } catch (e) {
      console.error('Failed to fetch signal history', e);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [mode, selectedBot, strategyFilter, strategyType]);

  const fmtDateTime = (dateStr: string) => formatKstTime(dateStr);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card p-6 rounded-3xl border border-border shadow-sm">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-foreground">매매 체결 이력</h2>
          <p className="text-muted-foreground text-sm font-medium">시그널 봇의 개별 진입 및 청산 히스토리를 확인합니다.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button variant={strategyFilter === 'all' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('all')}>전체 봇</Button>
            <Button variant={strategyFilter === 'manual' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('manual')}>🛠️ 수동/지표</Button>
            <Button variant={strategyFilter === 'auto' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('auto')}>🚀 자율 스캐너</Button>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Select value={strategyType} onValueChange={(v) => { setStrategyType(v); setPage(1); }}>
              <SelectTrigger className="h-8 px-3 text-[10px] font-bold border-0 bg-transparent shadow-none focus:ring-0 w-[130px]">
                <SelectValue placeholder="전략 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs font-bold">📊 전체 전략</SelectItem>
                {availableStrategies.map((s) => (
                  <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button variant={mode === 'all' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('all')}>전체 모드</Button>
            <Button variant={mode === 'mainnet' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('mainnet')}>실전</Button>
            <Button variant={mode === 'testnet' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('testnet')}>테스트넷</Button>
            <Button variant={mode === 'paper' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('paper')}>SIM</Button>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl border-border bg-background h-10" onClick={() => fetchData(page)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> 새로고침
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-card rounded-2xl p-12 text-center text-muted-foreground border border-border">데이터 로딩 중...</div>
      ) : histories.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 flex flex-col items-center text-center gap-3">
          <Layers className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-muted-foreground font-medium">체결된 매매 이력이 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <Card className="border border-border shadow-xl rounded-2xl overflow-hidden bg-card">
            <Table>
              <TableHeader className="bg-muted/30 border-b border-border">
                <TableRow>
                  <TableHead className="font-bold text-muted-foreground">봇 / 종목</TableHead>
                  <TableHead className="font-bold text-muted-foreground">체결 단가 / 레버리지</TableHead>
                  <TableHead className="font-bold text-muted-foreground">체결 수량 / 포지션 규모</TableHead>
                  <TableHead className="font-bold text-muted-foreground">실현 PnL / 수수료</TableHead>
                  <TableHead className="font-bold text-muted-foreground">체결 시간 / 사유</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {histories.map((t) => {
                  const isLong = (t.side || 'LONG').toLowerCase() === 'long';
                  return (
                    <TableRow key={t.id} className="hover:bg-muted/10 transition-colors border-border">
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-xl ${isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                            {isLong ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                          </div>
                          <div className="flex flex-col">
                            <div 
                              className="font-black text-foreground flex items-center gap-1.5 leading-none cursor-pointer hover:text-primary transition-colors hover:underline"
                              onClick={() => router.get(`/signal/${t.bot_id}`)}
                            >
                              <span className="text-[10px] text-muted-foreground font-bold">#{t.bot_id}</span>
                              {t.bot_name}
                              {t.bot_source === 'auto_scanner' && (
                                <Badge className="bg-primary/20 text-primary border-primary/30 text-[9px] h-4 px-1.5 font-black shrink-0">🤖 AUTO</Badge>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight mt-1 flex items-center gap-1.5">
                              {!t.is_live ? (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 text-[8px] h-3.5 px-1 font-black">PAPER</Badge>
                              ) : t.use_testnet ? (
                                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[8px] h-3.5 px-1 font-black">TEST</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-success/10 text-success border-success/20 text-[8px] h-3.5 px-1 font-black">LIVE</Badge>
                              )}
                              <Badge variant="outline" className="bg-muted text-foreground border-none text-[8px] h-3.5 px-1 font-bold">{t.strategy_type}</Badge>
                            </div>
                            <p className="text-[10px] text-muted-foreground font-bold mt-0.5">{t.symbol}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs font-black text-foreground">${formatPrice(t.price)}</p>
                        <Badge variant="outline" className={`text-[9px] py-0 h-4 w-fit mt-1 border-border ${isLong ? 'bg-success/5 text-success' : 'bg-danger/5 text-danger'}`}>
                          {t.side} {t.leverage}x
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <p className="text-xs font-black text-foreground">{formatQty(t.qty)} <span className="text-[10px] font-bold text-muted-foreground">Qty</span></p>
                          <p className="text-[11px] font-bold text-primary">{isPrivate ? '****' : `$${formatPrice(t.price * t.qty, 2)}`} <span className="text-[9px] text-muted-foreground opacity-60">Notional</span></p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <p className={`text-xs font-black ${t.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                            {isPrivate ? '****' : `${t.net_pnl >= 0 ? '+' : ''}${formatPrice(t.net_pnl, 4)} USDT`}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">Fee: {isPrivate ? '****' : `$${formatPrice(t.fee, 4)}`}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs font-bold text-foreground">{fmtDateTime(t.created_at)}</p>
                        <p className="text-[10px] text-muted-foreground italic mt-0.5 max-w-[200px] truncate">{t.reason || '청산 완료'}</p>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {total > per && (
            <div className="flex items-center justify-between px-6 py-4 bg-muted/10 rounded-2xl mt-4 border border-border">
              <div className="text-[11px] text-muted-foreground font-black uppercase tracking-widest pl-2">
                총 {total.toLocaleString()}건 중 {(page - 1) * per + 1}~{Math.min(page * per, total)} 표시
              </div>
              <div className="flex items-center gap-1.5">
                <Button variant="outline" size="sm" onClick={() => { setPage(1); fetchData(1); }} disabled={page === 1 || loading} className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all border-border" title="맨처음">
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => { const newPage = Math.max(1, page - 1); setPage(newPage); fetchData(newPage); }} disabled={page === 1 || loading} className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all border-border">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center justify-center h-9 px-4 bg-background border border-border rounded-xl text-xs font-black text-primary min-w-[80px]">
                  {page} / {Math.ceil(total / per)}
                </div>
                <Button variant="outline" size="sm" onClick={() => { const newPage = page + 1; setPage(newPage); fetchData(newPage); }} disabled={page >= Math.ceil(total / per) || loading} className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all border-border">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => { const lastPage = Math.ceil(total / per); setPage(lastPage); fetchData(lastPage); }} disabled={page >= Math.ceil(total / per) || loading} className="h-9 w-9 p-0 rounded-xl hover:bg-primary/10 hover:text-primary transition-all border-border" title="맨끝">
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
